import React, { useEffect, useState } from "react";
import { Trash2, Eraser, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function DeleteUserButton({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    if (!window.confirm(`Permanently delete ${user.name} (${user.email}) and everything they created (developments, units, messages, tickets)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`Deleted ${user.name}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  return (
    <button data-testid={`user-delete-${user.id}`} disabled={busy} onClick={run} className="p-1.5 rounded-full text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60" aria-label="Delete user">
      <Trash2 size={14} />
    </button>
  );
}

export default function CleanupDialog({ open, onClose, onDone }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [keep, setKeep] = useState([]);
  const [plan, setPlan] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirm(""); setPlan(null);
    (async () => {
      const { data } = await api.get("/admin/users");
      setUsers(data);
      setKeep([me.id]);
    })();
  }, [open, me]);

  useEffect(() => {
    if (!open || !keep.length) return;
    const t = setTimeout(async () => {
      const { data } = await api.post("/admin/cleanup/preview", { keep_ids: keep });
      setPlan(data);
    }, 200);
    return () => clearTimeout(t);
  }, [keep, open]);

  const toggle = (id) => id !== me.id && setKeep((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));

  async function run() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup/run", { keep_ids: keep, confirm });
      toast.success(`Removed ${data.removed.users || 0} accounts and ${data.removed.developments || 0} developments`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eraser size={18} /> Remove test data</DialogTitle>
          <DialogDescription>Tick the accounts to <b>keep</b>. Every other account is permanently deleted, along with its developments, units, interests, conversations, messages, notifications, tickets and documents.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-1 px-1 divide-y divide-neutral-100 border border-neutral-100 rounded-2xl">
          {users.map((u) => {
            const isMe = u.id === me.id;
            const kept = keep.includes(u.id);
            return (
              <label key={u.id} data-testid={`cleanup-row-${u.id}`} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${kept ? "bg-propera-light/40" : "hover:bg-neutral-50"}`}>
                <input type="checkbox" data-testid={`cleanup-keep-${u.id}`} checked={kept} disabled={isMe} onChange={() => toggle(u.id)} className="w-4 h-4 accent-[#0E8C5A]" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{u.name} {isMe && <span className="text-[10px] text-neutral-500 font-medium ml-1 inline-flex items-center gap-0.5"><Lock size={9} /> you</span>}</span>
                  <span className="block text-xs text-neutral-500 truncate">{u.email} · {u.role}</span>
                </span>
                <span className={`text-[11px] font-semibold ${kept ? "text-propera" : "text-red-600"}`}>{kept ? "Keep" : "Delete"}</span>
              </label>
            );
          })}
        </div>
        {plan && (
          <p className="text-sm text-neutral-700" data-testid="cleanup-summary">
            Keeping <b>{plan.keep.length}</b> account(s). Deleting <b className="text-red-600">{plan.remove.length}</b> account(s) and <b className="text-red-600">{plan.developments_removed}</b> development(s).
          </p>
        )}
        <input data-testid="cleanup-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Type DELETE to confirm' className="w-full px-3 py-2 rounded-xl border border-neutral-200 focus:border-red-400 outline-none text-sm" />
        <DialogFooter className="gap-2">
          <button data-testid="cleanup-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-neutral-200 text-sm font-semibold">Cancel</button>
          <button data-testid="cleanup-run" disabled={busy || confirm !== "DELETE" || !plan} onClick={run} className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">{busy ? "Removing…" : "Permanently remove"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
